import { answerKey } from "@gierki/shared";
import { Karta, Komunikat, Odznaka, Przycisk } from "../ui.tsx";
import type { PmView } from "./PanstwaMiasta.tsx";

/**
 * Głosowanie nad zakwestionowanymi odpowiedziami.
 *
 * Trafiają tu wyłącznie te odpowiedzi, którym ktoś zgłosił sprzeciw — zwykle
 * jedna albo dwie na rundę, często żadna (wtedy ten ekran się nie pokazuje).
 *
 * Nad własną odpowiedzią się nie głosuje. Remis oznacza uznanie: w grze
 * rodzinnej lepiej przepuścić wątpliwe słowo niż wywołać awanturę.
 */
export function Glosowanie({
  pm,
  nicki,
  mojeId,
  onGlos,
  onWerdykt,
  onGotowe,
}: {
  pm: PmView;
  nicki: Record<string, string>;
  mojeId: string;
  onGlos: (key: string, accept: boolean) => void;
  onWerdykt: (key: string, accept: boolean) => void;
  onGotowe: () => void;
}) {
  const jestemHostem = pm.hostId === mojeId;
  const jestemGotowy = pm.ready.includes(mojeId);

  const sporne = Object.entries(pm.objections)
    .filter(([, kwestionujacy]) => kwestionujacy.length > 0)
    .map(([key]) => {
      const wpis = pm.evaluations.find(
        (evaluation) => answerKey(evaluation.playerId, evaluation.category) === key,
      );
      return wpis ? { key, wpis } : null;
    })
    .filter((item): item is { key: string; wpis: PmView["evaluations"][number] } => item !== null);

  return (
    <>
      <Komunikat>
        {sporne.length === 1 ? "Jedna odpowiedź budzi wątpliwości." : `${sporne.length} odpowiedzi budzą wątpliwości.`}{" "}
        Uznajecie je czy nie?
      </Komunikat>

      {sporne.map(({ key, wpis }) => {
        const glosy = pm.votes[key] ?? {};
        const mojGlos = glosy[mojeId];
        const zaliczam = Object.values(glosy).filter(Boolean).length;
        const odrzucam = Object.values(glosy).length - zaliczam;
        const mojaOdpowiedz = wpis.playerId === mojeId;
        const kategoria = pm.categories.find((item) => item.key === wpis.category);

        return (
          <Karta key={key}>
            <div className="text-sm text-tekst-slaby">
              {kategoria?.label ?? wpis.category} · {nicki[wpis.playerId] ?? "?"}
            </div>
            <div className="text-2xl font-semibold my-2">{wpis.display}</div>

            {mojaOdpowiedz ? (
              <div className="text-sm text-tekst-slaby">
                To Twoja odpowiedź — decydują pozostali.
              </div>
            ) : (
              <div className="flex gap-2">
                <Przycisk
                  wariant={mojGlos === true ? "glowny" : "zwykly"}
                  onClick={() => onGlos(key, true)}
                  className="flex-1"
                >
                  Uznaję
                </Przycisk>
                <Przycisk
                  wariant={mojGlos === false ? "blad" : "zwykly"}
                  onClick={() => onGlos(key, false)}
                  className="flex-1"
                >
                  Nie uznaję
                </Przycisk>
              </div>
            )}

            <div className="flex gap-2 mt-3 items-center">
              <Odznaka aktywna={zaliczam > odrzucam}>za: {zaliczam}</Odznaka>
              <Odznaka>przeciw: {odrzucam}</Odznaka>
            </div>

            {jestemHostem ? (
              <div className="mt-3 pt-3 border-t border-obrys">
                <div className="text-xs text-tekst-slaby mb-2">Decyzja hosta (nadpisuje głosy)</div>
                <div className="flex gap-2">
                  <Przycisk onClick={() => onWerdykt(key, true)} className="flex-1">
                    Zalicz
                  </Przycisk>
                  <Przycisk onClick={() => onWerdykt(key, false)} className="flex-1">
                    Odrzuć
                  </Przycisk>
                </div>
              </div>
            ) : null}
          </Karta>
        );
      })}

      <div className="sticky bottom-0 pt-2 bezpieczny-dol bg-tlo">
        <Przycisk
          wariant={jestemGotowy ? "gorny" : "glowny"}
          pelna
          onClick={onGotowe}
          disabled={jestemGotowy}
          className="min-h-16"
        >
          {jestemGotowy ? `Czekam (${pm.ready.length}/${pm.progress.length})` : "Gotowe"}
        </Przycisk>
      </div>
    </>
  );
}
